import { Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertButton, AlertController, AlertInput, ModalController, PopoverController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TeamService } from '../../../_core/services/team.service';
import { ToastService } from '../../../_core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
    selector: 'app-ita-team-getter',
    templateUrl: './ita-team-getter.component.html',
    styleUrls: ['./ita-team-getter.component.scss'],
})
export class ItaTeamGetterComponent implements OnInit {
    private translateService = inject(TranslateService);
    @Input() groupId;
    form: UntypedFormGroup;

    private readonly destroy$: Subject<void> = new Subject();
    isSubmitting: boolean;

    constructor(
        private fb: UntypedFormBuilder,
        private toast: ToastService,
        private modalController: ModalController,
        private teamService: TeamService,
        private alertController: AlertController
    ) { }

    ngOnInit() {
        this.buildForm();
    }

    buildForm() {
        this.form = this.fb.group({
            username: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required]],
            partner: ['ITA']
        });
    }

    onSubmit() {
        if (this.form.invalid) {
            this.toast.presentDangerToast(this.translateService.instant('Username and password are required'));
            return;
        }

        this.isSubmitting = true;

        const credentials = this.form.value;
        this.teamService
            .getItaTeam(credentials)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSubmitting = false)
            )
            .subscribe({
                next: res => {
                    const teams = res.data.teams;
                    const partner = res.data.partner;
                    this.handleTeams(teams, partner, credentials);
                },
            });
    }

    async handleTeams(teams: any[], partner, credentials) {

        if (this.groupId) {
            // This is used when a coach has to identify himself for the start of new season
            // It returns value to team details component
            const index = teams.findIndex(t => t.groupId === this.groupId);
            return index > -1
                ? await this.modalController.dismiss(teams[index])
                : await this.modalController.dismiss();

        } else {
            if (teams.length > 1) {
                const inputs: AlertInput[] = [];

                teams.forEach((team, i) => {
                    inputs.push({
                        type: 'radio',
                        label: team.groupName,
                        value: i
                    });
                });

                const alert = await this.alertController.create({
                    header: this.translateService.instant('Select team'),
                    inputs: inputs,
                    buttons: [
                        {
                            text: this.translateService.instant('Import'),
                            handler: async (index) => {
                                await this.modalController.dismiss({ team: teams[index], partner, credentials });
                            }
                        },
                        {
                            text: this.translateService.instant('Cancel')
                        }
                    ]
                });

                await alert.present();
            } else if (teams.length === 1) {
                this.modalController.dismiss({ team: teams[0], partner, credentials });
            } else {
                const alert = await this.alertController.create({
                    header: this.translateService.instant('Teams are not found!'),
                    message: this.translateService.instant('Make sure that your account is associated with teams on ITA platform. If you believe there is a problem send us a message through feedback form in the side menu.'),
                    buttons: [
                        {
                            text: this.translateService.instant('Ok'),
                        }
                    ]
                });

                await alert.present();
            }
        }

    }

    dismiss() {
        this.modalController.dismiss();
    }

}
